'use client';

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Lightbulb, ThumbsUp, DollarSign, HelpCircle, Menu, X, MousePointerClick } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const navLinks = [
  { href: "#solution", label: "Solução", icon: Lightbulb },
  { href: "#benefits", label: "Benefícios", icon: ThumbsUp },
  { href: "#pricing", label: "Planos", icon: DollarSign },
  { href: "#faq", label: "FAQ", icon: HelpCircle },
];

export default function Header() {
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Set initial state
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300",
        hasScrolled
          ? "bg-primary/70 backdrop-blur-md border-b border-primary-darker/20 shadow-lg"
          : "bg-primary"
      )}
    >
      <div className="container mx-auto flex h-24 items-center justify-between">
        <div className="flex items-center gap-6">
          <nav className="hidden items-center gap-1 md:flex">
            <ul className="flex items-center gap-1">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="rounded-md px-3 py-2 text-sm font-medium text-primary-foreground/90 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        
        <div className="flex items-center gap-2">
            <Link href="#cta" className="hidden md:flex items-center gap-2 rounded-xl justify-center text-center text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-green-300 text-black hover:bg-green-400 h-auto py-3 px-6 sm:px-8 sm:h-11">
              <MousePointerClick className="h-5 w-5" />
              <span>Acessar demonstração</span>
            </Link>

          <div className="md:hidden flex items-center w-full justify-start gap-2">
              <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="secondary" size="icon">
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Abrir menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[80%] max-w-sm p-0 bg-primary flex flex-col">
                  <SheetHeader className="p-6 flex flex-row items-center justify-between border-b border-primary-darker/20">
                    <SheetTitle className="text-primary-foreground">Menu</SheetTitle>
                    <SheetClose>
                      <X className="h-6 w-6 text-primary-foreground/80 hover:text-primary-foreground" />
                      <VisuallyHidden>Fechar menu</VisuallyHidden>
                    </SheetClose>
                  </SheetHeader>
                  <div className="flex flex-col h-full bg-card flex-1">
                    <nav className="flex-1 p-6">
                      <ul className="flex flex-col gap-2">
                        {navLinks.map((link) => (
                          <li key={link.href}>
                            <Link
                              href={link.href}
                              onClick={() => setIsMenuOpen(false)}
                              className="flex items-center gap-4 rounded-md p-3 text-base font-medium text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground"
                            >
                              <link.icon className="h-5 w-5 text-primary" />
                              <span>{link.label}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </nav>
                    <div className="p-6 mt-auto bg-card">
                      <Button asChild size="lg" className="w-full" variant="default">
                        <Link href="#cta" onClick={() => setIsMenuOpen(false)}>
                          <MousePointerClick className="h-5 w-5" />
                          Acessar demonstração
                          </Link>
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              <div className="flex-grow"></div>
              <Link href="#cta" className="flex items-center gap-2 rounded-xl justify-center text-center text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-green-300 text-black hover:bg-green-400 h-auto py-3 px-4">
                <MousePointerClick className="h-5 w-5" />
                <span>Acessar demonstração</span>
              </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
